import { createContext, useContext, useMemo } from "react";
import type * as React from "react";
import type { WidgetSettingField, WidgetSettingOption } from "./types";
import type { Omittable } from "../../lib/props";

/**
 * Props a custom settings control receives. A control owns rendering ONE field of
 * an open `kind`; it reads/writes the value through `onCommit(field.key, value)`.
 */
export type WidgetControlProps = Omittable<{
  field: WidgetSettingField;
  value: unknown;
  disabled?: boolean;
  onCommit: (key: string, value: unknown) => void;
}>;

/**
 * The extensibility seam: a consumer registers controls for open field `kind`s
 * and option sources for `dynamicOptions` names. {@link SettingsFields} renders
 * base kinds natively and dispatches everything else here, so a new domain
 * control is added without touching the kit. Option sources are hooks —
 * {@link SettingsFields} calls every registered source once, unconditionally, to
 * keep hook order stable.
 */
export type WidgetControls = {
  controls: Record<string, React.FC<WidgetControlProps>>;
  optionSources: Record<string, () => WidgetSettingOption[]>;
  /**
   * The label a `dynamicOptions` source gives a stored value, readable OUTSIDE
   * a settings form — plain functions, not hooks, so a title resolver can call
   * them for any source by name. Keyed like `optionSources`; a source with no
   * entry answers no label. Optional: a consumer whose titles never name a
   * picked option registers none.
   */
  optionLabels?: Record<string, (value: string) => string | undefined>;
};

/**
 * What a module's `dynamicTitle` may read beyond its config: the display
 * label a `dynamicOptions` source carries for a stored value (a picked
 * option's name), so a derived title can say "by Bid Type" rather than "by
 * field". `undefined` when the source is unknown or the value is not one of
 * its options.
 */
export type WidgetTitleContext = {
  optionLabel: (source: string, value: string) => string | undefined;
};

/** The context a title resolves against with no provider — no labels at all. */
export const EMPTY_TITLE_CONTEXT: WidgetTitleContext = { optionLabel: () => undefined };

const ControlContext = createContext<WidgetControls>({ controls: {}, optionSources: {} });

export const WidgetControlsProvider = ControlContext.Provider;

export function useWidgetControls(): WidgetControls {
  return useContext(ControlContext);
}

/** The title context the mounted controls provide — `optionLabels` folded
 *  into one lookup. Stable while the controls value is. */
export function useWidgetTitleContext(): WidgetTitleContext {
  const { optionLabels } = useWidgetControls();
  return useMemo<WidgetTitleContext>(
    () =>
      optionLabels
        ? { optionLabel: (source, value) => optionLabels[source]?.(value) }
        : EMPTY_TITLE_CONTEXT,
    [optionLabels],
  );
}
